import { useCallback, useEffect, useState } from 'react'
import { MutateBooleanListCustomFieldValuesInput } from 'schema'
import { CIMSBasicFieldProps } from '../../../CIMSBasicFields/types'
import { CIMSTag } from '../../../CIMSTag'
import { useFoundCallback } from './useFoundCallback'
import { CIMSTags } from '../../../CIMSTags'
import { Space } from 'antd'
import CIMSCheckbox from '../../../CIMSCheckbox'
import { useProjectCustomFieldsContext } from '../../ProjectCustomFieldsContext'
import { ListValues, DisabledStates } from '../../types'
import { useKeyedBooleans } from '../../../../hooks/useKeyedBooleans'

export function useListCustomFields({
  listValues,
  onCheck,
  alreadyDisabled,
  recordInactive,
}: UseListCustomFieldsOptions): CIMSBasicFieldProps[] {
  const [fields, setFields] = useState<CIMSBasicFieldProps[]>([])
  const found = useFoundCallback()
  const { renderedAsCheckboxes, disabledById } = useProjectCustomFieldsContext()
  const [loading, { add, remove }] = useKeyedBooleans()
  const onChange = useCallback<
    (
      options: MutateBooleanListCustomFieldValuesInput & {
        customListItemId: string
      }
    ) => void | Promise<void>
  >(
    async ({ booleans, customFieldId, customListItemId }) => {
      const key = `${customFieldId}-${customListItemId}`
      add(key)
      await onCheck?.({
        customFieldId,
        booleans: (booleans ?? []).map(({ customListItemId: id, checked }) => {
          if (id === customListItemId) checked = !checked
          return { customListItemId: id, checked }
        }),
      })
      remove(key)
    },
    /**
     * adding remove and add here causes an infinite loop
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCheck]
  )
  useEffect(() => {
    if (!listValues) return
    setFields([
      ...(listValues.singles ?? []).map(
        ({ customFieldId, customField, customListItem }) =>
          found({
            customField,
            customFieldId,
            children: <CIMSTag>{customListItem?.value}</CIMSTag>,
            hidden: !customListItem,
          })
      ),
      ...(listValues.multiples ?? []).map(
        ({ customField, customFieldId, customListItems }) =>
          found({
            customField,
            customFieldId,
            children: (
              <CIMSTags
                tags={customListItems.map(({ value }) => ({ text: value }))}
              />
            ),
            hidden: !customListItems.length,
          })
      ),
      ...(renderedAsCheckboxes?.checkboxes ?? []).map(
        ({ id: customFieldId, name, description }) =>
          found({
            hidden: !renderedAsCheckboxes?.checkboxes.length,
            customField: { name, description },
            customFieldId,
            children: (found) => {
              const booleans = listValues?.checkboxes?.find(
                (checkbox) => checkbox.customFieldId === customFieldId
              )?.booleans
              return (
                <Space size="large">
                  {(found?.customListItems ?? [])
                    .reduce((items, { id, value }) => {
                      const checked =
                        booleans?.find(
                          ({ customListItemId }) => id === customListItemId
                        )?.checked ?? false
                      items.push({ checked, customListItemId: id, value })
                      return items
                    }, Array<{ checked: boolean; customListItemId: string; value: string }>())
                    .map(({ checked, customListItemId, value }, _, reduced) => {
                      const key = `${customFieldId}-${customListItemId}`
                      return (
                        <Space key={key} size="small">
                          <CIMSTag>{value}</CIMSTag>
                          <CIMSCheckbox
                            checked={checked}
                            disabled={disabledById({
                              customFieldId,
                              alreadyDisabled,
                              recordInactive,
                            })}
                            loading={loading(key)}
                            onChange={async () =>
                              await onChange?.({
                                customFieldId,
                                customListItemId,
                                booleans: reduced,
                              })
                            }
                          />
                        </Space>
                      )
                    })}
                </Space>
              )
            },
          })
      ),
    ])
  }, [
    listValues,
    found,
    loading,
    onChange,
    renderedAsCheckboxes?.checkboxes,
    disabledById,
    alreadyDisabled,
    recordInactive,
  ])
  return fields
}

export interface UseListCustomFieldsOptions extends DisabledStates {
  listValues?: ListValues
  onCheck?: (
    options: MutateBooleanListCustomFieldValuesInput
  ) => void | Promise<void>
}
